/**
 * TabPanelTool - Tab页工具组件
 * 提供可配置的Tab页切换功能，支持根据tab id动态设置content内容
 * 基于TabPanelUI扩展，专门用于工具面板场景
 */

import { globalManager } from '@/utils/globalConfig';
import { BootstrapUI, bootstrapUI, ElementOptions } from '../bootstrapui';
import { getI18nText } from '@/utils/commonFunc';

export interface TabPanelToolItemOptions {
    /** Tab ID */
    id: string;
    /** Tab标题 */
    title: string;
    /** Tab内容 - 可以是字符串、HTMLElement或函数 */
    content?: string | HTMLElement | (() => string | HTMLElement);
    /** 图标名称 */
    iconName?: string;
    /** 是否只显示图标 */
    iconOnly?: boolean;
    /** 是否禁用 */
    disabled?: boolean;
    /** 自定义样式类 */
    className?: string;
    /** 是否可关闭 */
    closable?: boolean;
    /** 是否为默认激活Tab */
    isDefault?: boolean;
    /** 内容是否懒加载 */
    lazyLoad?: boolean;
    /** Tab描述 */
    description?: string;
    /** 是否显示在工具栏 */
    showInToolbar?: boolean;
    /** 标题多语言键 */
    titleI18nKey?: string;
}

export class TabPanelToolItem {
    /** Tab ID */
    id: string;
    /** Tab标题 */
    title: string;
    /** Tab内容 */
    content?: string | HTMLElement | (() => string | HTMLElement);
    /** 图标名称 */
    iconName?: string;
    /** 是否只显示图标 */
    iconOnly?: boolean;
    /** 是否禁用 */
    disabled?: boolean;
    /** 自定义样式类 */
    className?: string;
    /** 是否可关闭 */
    closable?: boolean;
    /** 是否为默认激活Tab */
    isDefault?: boolean;
    /** 内容是否懒加载 */
    lazyLoad?: boolean;
    /** Tab描述 */
    description?: string;
    /** 是否显示在工具栏 */
    showInToolbar?: boolean;
    /** 标题多语言键 */
    titleI18nKey?: string;

    constructor(options: TabPanelToolItemOptions) {
        this.id = options.id;
        this.title = options.title;
        this.content = options.content;
        this.iconName = options.iconName;
        this.iconOnly = options.iconOnly || false;
        this.disabled = options.disabled || false;
        this.className = options.className;
        this.closable = options.closable;
        this.isDefault = options.isDefault || false;
        this.lazyLoad = options.lazyLoad || false;
        this.description = options.description;
        this.showInToolbar = options.showInToolbar !== false;
        this.titleI18nKey = options.titleI18nKey||'';
    }
}

export interface TabPanelToolOptions extends ElementOptions {
    /** Tab列表 */
    items: TabPanelToolItem[];
    /** Tab布局方向 */
    tabPosition?: 'top' | 'left' | 'right';
    /** 默认激活的Tab ID */
    defaultActiveId?: string;
    /** Tab容器样式类 */
    tabContainerClass?: string;
    /** Tab按钮样式类 */
    tabButtonClass?: string;
    /** Tab内容容器样式类 */
    contentContainerClass?: string;
    /** Tab内容样式类 */
    contentClass?: string;
    /** 激活状态样式类 */
    activeClass?: string;
    /** 是否显示Tab边框 */
    showBorder?: boolean;
    /** Tab间距 */
    tabGap?: string;
    /** 是否可关闭Tab */
    allowClose?: boolean;
    /** Tab高度 */
    tabHeight?: string;
    /** 内容区域最小高度 */
    contentMinHeight?: string;
    /** 是否显示工具栏 */
    showToolbar?: boolean;
    /** 工具栏样式类 */
    toolbarClass?: string;
    /** Tab切换回调 */
    onTabChange?: (item: TabPanelToolItem, index: number) => void;
    /** Tab关闭回调 */
    onTabClose?: (item: TabPanelToolItem, index: number) => void;
    /** 内容更新回调 */
    onContentUpdate?: (item: TabPanelToolItem, content: string | HTMLElement) => void;
}

export class TabPanelToolUI {
    private ui: BootstrapUI;
    private container: HTMLElement;
    private tabContainer: HTMLElement;
    private contentContainer: HTMLElement;
    private toolbar?: HTMLElement;
    private options: TabPanelToolOptions;
    private items: TabPanelToolItem[] = [];
    private activeId: string | null = null;
    private tabButtons: Map<string, HTMLElement> = new Map();
    private contentElements: Map<string, HTMLElement> = new Map();
    private loadedContents: Map<string, boolean> = new Map();
    private onChangeCallbacks: ((item: TabPanelToolItem, index: number) => void)[] = [];
    private onCloseCallbacks: ((item: TabPanelToolItem, index: number) => void)[] = [];
    private onContentUpdateCallbacks: ((item: TabPanelToolItem, content: string | HTMLElement) => void)[] = [];

    constructor(options: TabPanelToolOptions) {
        this.ui = bootstrapUI;
        this.options = {
            tabPosition: 'top',
            showBorder: true,
            allowClose: false,
            tabHeight: 'auto',
            contentMinHeight: '200px',
            activeClass: 'active',
            showToolbar: false,
            ...options
        };

        this.items = this.options.items || [];
        this.container = this.createContainer();
        this.tabContainer = this.createTabContainer();
        this.contentContainer = this.createContentContainer();
        
        // 创建工具栏（如果需要）
        if (this.options.showToolbar) {
            this.toolbar = this.createToolbar();
        }
        
        // 添加选项中的回调函数
        if (this.options.onTabChange) {
            this.onChangeCallbacks.push(this.options.onTabChange);
        }
        if (this.options.onTabClose) {
            this.onCloseCallbacks.push(this.options.onTabClose);
        }
        if (this.options.onContentUpdate) {
            this.onContentUpdateCallbacks.push(this.options.onContentUpdate);
        }
        
        this.setupEventListeners();
        this.populateTabs();
        
        // 设置默认激活Tab
        if (this.options.defaultActiveId) {
            this.setActiveTab(this.options.defaultActiveId);
        } else if (this.items.length > 0) {
            // 如果没有指定默认Tab，激活第一个Tab
            const defaultItem = this.items.find(item => item.isDefault) || this.items[0];
            this.setActiveTab(defaultItem.id);
        }
    }

    /**
     * 创建主容器
     */
    private createContainer(): HTMLElement {
        const container = this.ui.createElement({
            className: [
                'vision-tab-panel-tool',
                ...(this.options.showBorder ? ['vision-tab-panel-tool-bordered'] : []),
                ...(Array.isArray(this.options.className) ? this.options.className : this.options.className ? [this.options.className] : [])
            ],
            ...this.options
        });

        return container;
    }

    /**
     * 创建Tab容器
     */
    private createTabContainer(): HTMLElement {
        const tabContainer = this.ui.createElement({
            className: [
                'vision-tab-tool-container',
                `vision-tab-tool-container-${this.options.tabPosition || 'top'}`,
                ...(Array.isArray(this.options.tabContainerClass) ? this.options.tabContainerClass : this.options.tabContainerClass ? [this.options.tabContainerClass] : [])
            ],
            styles: {
                gap: this.options.tabGap || '0'
            }
        });

        this.container.appendChild(tabContainer);
        return tabContainer;
    }

    /**
     * 创建内容容器
     */
    private createContentContainer(): HTMLElement {
        const contentContainer = this.ui.createElement({
            className: [
                'vision-tab-tool-content-container',
                ...(Array.isArray(this.options.contentContainerClass) ? this.options.contentContainerClass : this.options.contentContainerClass ? [this.options.contentContainerClass] : [])
            ],
            styles: {
                minHeight: this.options.contentMinHeight || '200px'
            }
        });

        this.container.appendChild(contentContainer);
        return contentContainer;
    }

    /**
     * 创建工具栏
     */
    private createToolbar(): HTMLElement {
        const toolbar = this.ui.createElement({
            className: [
                'vision-tab-tool-toolbar',
                ...(Array.isArray(this.options.toolbarClass) ? this.options.toolbarClass : this.options.toolbarClass ? [this.options.toolbarClass] : [])
            ]
        });

        this.container.insertBefore(toolbar, this.tabContainer);
        return toolbar;
    }

    /**
     * 设置事件监听器
     */
    private setupEventListeners(): void {
        // 可以在这里添加全局事件监听器
        this.tabContainer.addEventListener('click', (event) => {
            // console.log('tabContainer click -- 打开右侧控制面板', event);
            globalManager.openRightToolsBar();
        });
    }

    /**
     * 填充Tab
     */
    private populateTabs(): void {
        this.tabContainer.innerHTML = '';
        this.contentContainer.innerHTML = '';
        this.tabButtons.clear();
        this.contentElements.clear();

        this.items.forEach((item, index) => {
            const tabButton = this.createTabButton(item, index);
            const contentElement = this.createContentElement(item, index);
            
            this.tabButtons.set(item.id, tabButton);
            this.contentElements.set(item.id, contentElement);
            
            this.tabContainer.appendChild(tabButton);
            this.contentContainer.appendChild(contentElement);
        });
    }

    /**
     * 创建Tab按钮
     */
    private createTabButton(item: TabPanelToolItem, index: number): HTMLElement {
        const button = this.ui.createElement({
            tag: 'button',
            className: [
                'vision-tab-tool-button',
                ...(item.iconOnly ? ['vision-tab-tool-button-icon-only'] : []),
                ...(item.disabled ? ['disabled'] : []),
                ...(item.className ? [item.className] : []),
                ...(Array.isArray(this.options.tabButtonClass) ? this.options.tabButtonClass : this.options.tabButtonClass ? [this.options.tabButtonClass] : [])
            ],
            textContent: '',
            attributes: {
                type: 'button',
                'data-tab-id': item.id,
                'data-tab-index': index.toString(),
                'aria-selected': 'false',
                'role': 'tab',
                'title': item.description || item.title
            },
            styles: {
                height: this.options.tabHeight || 'auto'
            }
        });

        // 添加图标（如果有）
        if (item.iconName) {
            const icon = this.ui.createIcon({
                iconName: item.iconName,
                size: 'sm',
                className: 'vision-tab-tool-icon'
            });
            button.appendChild(icon);
        }

        if(item.titleI18nKey){
            button.setAttribute('title', getI18nText(item.titleI18nKey));
            button.setAttribute('data-i18n-title', item.titleI18nKey);
        }

        // 添加标题（如果不是仅图标模式）
        if (!item.iconOnly && item.title) {
            const titleSpan = this.ui.createElement({
                tag: 'span',
                textContent: item.title,
                className: 'vision-tab-tool-title'
            });
            button.appendChild(titleSpan);
        }

        // 添加关闭按钮（如果允许关闭）
        if (this.options.allowClose && item.closable !== false) {
            const closeButton = this.ui.createElement({
                tag: 'button',
                className: 'vision-tab-tool-close',
                textContent: '×',
                attributes: {
                    type: 'button',
                    'aria-label': '关闭Tab'
                }
            });
            button.appendChild(closeButton);

            // 关闭按钮点击事件
            closeButton.addEventListener('click', (event) => {
                event.stopPropagation();
                this.closeTab(item.id);
            });
        }

        // 添加点击事件
        if (!item.disabled) {
            button.addEventListener('click', (event) => {
                event.stopPropagation();
                this.handleTabClick(item, index);
            });
        }

        return button;
    }

    /**
     * 创建内容元素
     */
    private createContentElement(item: TabPanelToolItem, index: number): HTMLElement {
        const contentElement = this.ui.createElement({
            className: [
                'vision-tab-tool-content',
                ...(Array.isArray(this.options.contentClass) ? this.options.contentClass : this.options.contentClass ? [this.options.contentClass] : [])
            ],
            attributes: {
                'data-tab-id': item.id,
                'data-tab-index': index.toString(),
                'role': 'tabpanel',
                'aria-hidden': 'true'
            }
        });

        // 如果不是懒加载，立即设置内容
        if (!item.lazyLoad && item.content) {
            this.setContentForElement(contentElement, item.content);
        }

        return contentElement;
    }

    /**
     * 为内容元素设置内容
     */
    private setContentForElement(element: HTMLElement, content: string | HTMLElement | (() => string | HTMLElement)): void {
        // element.innerHTML = '';
        // console.log('为元素设置内容', content);
        
        if (typeof content === 'string') {
            element.innerHTML = content;
        } else if (content instanceof HTMLElement) {
            element.appendChild(content);
        } else if (typeof content === 'function') {
            const result = content();
            if (typeof result === 'string') {
                element.innerHTML = result;
            } else if (result instanceof HTMLElement) {
                element.appendChild(result);
            }
        }
    }

    /**
     * 处理Tab点击
     */
    private handleTabClick(item: TabPanelToolItem, index: number): void {
        this.setActiveTab(item.id);
        
        // 如果是懒加载且内容未加载，现在加载内容
        if (item.lazyLoad && !this.loadedContents.get(item.id) && item.content) {
            const contentElement = this.contentElements.get(item.id);
            if (contentElement) {
                this.setContentForElement(contentElement, item.content);
                this.loadedContents.set(item.id, true);
            }
        }
        
        // 执行变化回调
        this.onChangeCallbacks.forEach(callback => callback(item, index));
    }

    /**
     * 设置激活Tab
     */
    public setActiveTab(id: string): boolean {
        const item = this.items.find(item => item.id === id);
        if (!item || item.disabled) {
            return false;
        }

        // 取消所有Tab的激活状态
        this.tabButtons.forEach((button, buttonId) => {
            this.deactivateTab(buttonId);
        });

        // 激活指定Tab
        this.activateTab(id);
        this.activeId = id;
        globalManager.setActivePanelTab(id);
        return true;
    }

    /**
     * 激活Tab
     */
    private activateTab(id: string): void {
        const button = this.tabButtons.get(id);
        const content = this.contentElements.get(id);
        
        if (button) {
            button.classList.add(this.options.activeClass || 'active');
            button.setAttribute('aria-selected', 'true');
        }
        
        if (content) {
            content.classList.add('active');
            content.setAttribute('aria-hidden', 'false');
        }
    }

    /**
     * 取消激活Tab
     */
    private deactivateTab(id: string): void {
        const button = this.tabButtons.get(id);
        const content = this.contentElements.get(id);
        
        if (button) {
            button.classList.remove(this.options.activeClass || 'active');
            button.setAttribute('aria-selected', 'false');
        }
        
        if (content) {
            content.classList.remove('active');
            content.setAttribute('aria-hidden', 'true');
        }
    }

    /**
     * 根据Tab ID设置内容
     */
    public setTabContent(id: string, content: string | HTMLElement | (() => string | HTMLElement)): boolean {
        const item = this.items.find(item => item.id === id);
        if (!item) {
            return false;
        }

        // 更新item的content
        item.content = content;
        
        // 更新对应的内容元素
        const contentElement = this.contentElements.get(id);
        if (contentElement) {
            this.setContentForElement(contentElement, content);
            this.loadedContents.set(id, true);
            
            // 执行内容更新回调
            this.onContentUpdateCallbacks.forEach(callback => {
                if (typeof content === 'function') {
                    const result = content();
                    callback(item, typeof result === 'string' ? result : result);
                } else {
                    callback(item, content);
                }
            });
        }

        return true;
    }

    /**
     * 根据Tab ID获取内容
     */
    public getTabContent(id: string): string | HTMLElement | (() => string | HTMLElement) | null {
        const item = this.items.find(item => item.id === id);
        return item ? item.content || null : null;
    }

    /**
     * 根据Tab ID更新内容（支持函数形式）
     */
    public updateTabContent(id: string, contentUpdater: (currentContent: string | HTMLElement | (() => string | HTMLElement) | null) => string | HTMLElement | (() => string | HTMLElement)): boolean {
        const item = this.items.find(item => item.id === id);
        if (!item) {
            return false;
        }

        const newContent = contentUpdater(item.content || null);
        return this.setTabContent(id, newContent);
    }

    /**
     * 关闭Tab
     */
    public closeTab(id: string): boolean {
        const itemIndex = this.items.findIndex(item => item.id === id);
        if (itemIndex === -1) {
            return false;
        }

        const item = this.items[itemIndex];
        
        // 如果关闭的是当前激活的Tab，需要切换到其他Tab
        if (this.activeId === id) {
            const remainingItems = this.items.filter((_, index) => index !== itemIndex);
            if (remainingItems.length > 0) {
                this.setActiveTab(remainingItems[0].id);
            } else {
                this.activeId = null;
            }
        }

        // 移除Tab
        this.items.splice(itemIndex, 1);
        this.loadedContents.delete(id);
        
        // 重新渲染
        this.populateTabs();
        
        // 执行关闭回调
        this.onCloseCallbacks.forEach(callback => callback(item, itemIndex));

        return true;
    }

    /**
     * 添加Tab
     */
    public addTab(item: TabPanelToolItem, index?: number): void {
        if (index !== undefined && index >= 0 && index <= this.items.length) {
            this.items.splice(index, 0, item);
        } else {
            this.items.push(item);
        }
        
        this.populateTabs();
        
        // 如果是第一个Tab，自动激活
        if (this.items.length === 1 && !this.activeId) {
            this.setActiveTab(item.id);
        }
    }

    /**
     * 移除Tab
     */
    public removeTab(id: string): boolean {
        return this.closeTab(id);
    }

    /**
     * 更新Tab标题
     */
    public updateTabTitle(id: string, title: string): boolean {
        const item = this.items.find(item => item.id === id);
        if (!item) {
            return false;
        }

        item.title = title;
        
        const button = this.tabButtons.get(id);
        if (button && !item.iconOnly) {
            const titleElement = button.querySelector('.vision-tab-tool-title');
            if (titleElement) {
                titleElement.textContent = title;
            }
        }

        return true;
    }

    /**
     * 设置Tab禁用状态
     */
    public setTabDisabled(id: string, disabled: boolean): boolean {
        const item = this.items.find(item => item.id === id);
        const button = this.tabButtons.get(id);
        
        if (!item || !button) {
            return false;
        }

        item.disabled = disabled;
        
        if (disabled) {
            button.classList.add('disabled');
            button.setAttribute('disabled', 'true');
            
            // 如果当前激活的Tab被禁用，切换到其他Tab
            if (this.activeId === id) {
                const remainingItems = this.items.filter(item => !item.disabled && item.id !== id);
                if (remainingItems.length > 0) {
                    this.setActiveTab(remainingItems[0].id);
                } else {
                    this.activeId = null;
                }
            }
        } else {
            button.classList.remove('disabled');
            button.removeAttribute('disabled');
        }

        return true;
    }

    /**
     * 设置组件激活状态（启用/禁用整个组件）
     */
    public setActive(status: boolean): void {
        if (!status) {
            // 禁用整个组件
            this.container.classList.add('forbidden-event-dom');
            
            // 禁用所有Tab按钮
            this.tabButtons.forEach((button, buttonId) => {
                this.setTabDisabled(buttonId, true);
            });
        } else {
            // 启用整个组件
            this.container.classList.remove('forbidden-event-dom');
            
            // 启用所有Tab按钮
            this.tabButtons.forEach((button, buttonId) => {
                this.setTabDisabled(buttonId, false);
            });
        }
    }

    /**
     * 获取当前激活的Tab ID
     */
    public getActiveTabId(): string | null {
        return this.activeId;
    }

    /**
     * 获取当前激活的Tab项
     */
    public getActiveTab(): TabPanelToolItem | null {
        return this.items.find(item => item.id === this.activeId) || null;
    }

    /**
     * 获取所有Tab项
     */
    public getTabs(): TabPanelToolItem[] {
        return [...this.items];
    }

    /**
     * 获取Tab数量
     */
    public getTabCount(): number {
        return this.items.length;
    }

    /**
     * 添加Tab变化监听器
     */
    public onTabChange(callback: (item: TabPanelToolItem, index: number) => void): void {
        this.onChangeCallbacks.push(callback);
    }

    /**
     * 移除Tab变化监听器
     */
    public offTabChange(callback: (item: TabPanelToolItem, index: number) => void): void {
        const index = this.onChangeCallbacks.indexOf(callback);
        if (index > -1) {
            this.onChangeCallbacks.splice(index, 1);
        }
    }

    /**
     * 添加Tab关闭监听器
     */
    public onTabClose(callback: (item: TabPanelToolItem, index: number) => void): void {
        this.onCloseCallbacks.push(callback);
    }

    /**
     * 移除Tab关闭监听器
     */
    public offTabClose(callback: (item: TabPanelToolItem, index: number) => void): void {
        const index = this.onCloseCallbacks.indexOf(callback);
        if (index > -1) {
            this.onCloseCallbacks.splice(index, 1);
        }
    }

    /**
     * 添加内容更新监听器
     */
    public onContentUpdate(callback: (item: TabPanelToolItem, content: string | HTMLElement) => void): void {
        this.onContentUpdateCallbacks.push(callback);
    }

    /**
     * 移除内容更新监听器
     */
    public offContentUpdate(callback: (item: TabPanelToolItem, content: string | HTMLElement) => void): void {
        const index = this.onContentUpdateCallbacks.indexOf(callback);
        if (index > -1) {
            this.onContentUpdateCallbacks.splice(index, 1);
        }
    }

    /**
     * 获取容器元素
     */
    public getContainer(): HTMLElement {
        return this.container;
    }

    /**
     * 获取Tab容器元素
     */
    public getTabContainer(): HTMLElement {
        return this.tabContainer;
    }

    /**
     * 获取内容容器元素
     */
    public getContentContainer(): HTMLElement {
        return this.contentContainer;
    }

    /**
     * 获取工具栏元素
     */
    public getToolbar(): HTMLElement | undefined {
        return this.toolbar;
    }

    /**
     * 销毁组件
     */
    public destroy(): void {
        this.onChangeCallbacks = [];
        this.onCloseCallbacks = [];
        this.onContentUpdateCallbacks = [];
        this.tabButtons.clear();
        this.contentElements.clear();
        this.loadedContents.clear();
        
        if (this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}

// 导出默认实例创建函数
export function createTabPanelTool(options: TabPanelToolOptions): TabPanelToolUI {
    return new TabPanelToolUI(options);
}
